// ======================================================================
// SFV Tech | Material Collections — "Parts Ledger"
// ----------------------------------------------------------------------
// Design language: this is an electrician/solar-installer's stock ledger,
// not a generic admin dashboard. Copper (wire) and steel-blue (meter
// panel) are the two structural accents; status uses conventional LED
// colors (amber = pending, green = verified, red = rejected). Each
// ticket gets a small analog dial showing % returned, styled like a
// panel meter rather than a flat progress bar.
//
// Functional contract is unchanged from the previous version:
//   GET    /api/material-collections
//   POST   /api/material-collections/add
//   PATCH  /api/material-collections/:id
//   PATCH  /api/material-collections/:id/verify
//   DELETE /api/material-collections/:id
//   POST   /api/material-returns/add
// ======================================================================

import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box, Typography, Button, Stack, TextField, MenuItem, Menu, ListItemIcon,
  ListItemText, Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab,
  ThemeProvider, createTheme, CircularProgress,
} from "@mui/material";
import {
  ArrowBack, Logout, Search, Add, Undo, Edit, Delete, CheckCircle, Cancel,
  MoreVert, Bolt,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useSnackbar } from "notistack";
import api from "../../utils/api";

// ----------------------------------------------------------------------
// Design tokens
// ----------------------------------------------------------------------
const COPPER = "#e8944a";
const COPPER_DIM = "rgba(232,148,74,0.18)";
const STEEL = "#6fb3c9";
const STEEL_DIM = "rgba(111,179,201,0.16)";
const GOOD = "#4ec98a";
const BAD = "#e2645f";
const BG = "#181d20";
const PANEL = "#1e2427";
const PANEL_RAISED = "#242b2f";
const LINE = "#323b3f";
const TEXT = "#eef1f2";
const TEXT_DIM = "#8d9a9d";

const MANAGERS = ["admin", "storekeeper"];

const theme = createTheme({
  palette: {
    mode: "dark",
    background: { default: BG, paper: PANEL },
    primary: { main: COPPER },
    secondary: { main: STEEL },
    success: { main: GOOD },
    error: { main: BAD },
    warning: { main: COPPER },
    text: { primary: TEXT, secondary: TEXT_DIM },
    divider: LINE,
  },
  typography: {
    fontFamily: '"Inter", "Helvetica Neue", sans-serif',
    button: { textTransform: "none", fontWeight: 600 },
  },
  shape: { borderRadius: 10 },
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
    MuiDialog: { styleOverrides: { paper: { backgroundColor: PANEL_RAISED, border: `1px solid ${LINE}` } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: 8, backgroundColor: "rgba(255,255,255,0.02)" },
        notchedOutline: { borderColor: LINE },
      },
    },
    MuiInputLabel: { styleOverrides: { root: { color: TEXT_DIM } } },
    MuiMenu: { styleOverrides: { paper: { backgroundColor: PANEL_RAISED, border: `1px solid ${LINE}` } } },
  },
});

// ----------------------------------------------------------------------
// Fonts (condensed industrial display face for labels/eyebrows,
// monospace for quantities/dates so they read like a meter readout)
// ----------------------------------------------------------------------
function FontImport() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');

      @keyframes mc-rise {
        from { opacity: 0; transform: translateY(6px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      @media (prefers-reduced-motion: reduce) {
        .mc-row { animation: none !important; }
      }
      .mc-row { animation: mc-rise .28s ease both; }
      .mc-row:hover {
        border-color: ${COPPER} !important;
        box-shadow: 0 0 0 1px ${COPPER_DIM};
      }
      .mc-eyebrow {
        font-family: 'Oswald', sans-serif;
        letter-spacing: 0.14em;
        text-transform: uppercase;
      }
      .mc-mono { font-family: 'IBM Plex Mono', monospace; }
      .mc-scroll::-webkit-scrollbar { height: 8px; width: 8px; }
      .mc-scroll::-webkit-scrollbar-thumb { background: ${LINE}; border-radius: 8px; }
    `}</style>
  );
}

// ----------------------------------------------------------------------
// Status LED — small glowing dot + label, like an equipment indicator
// ----------------------------------------------------------------------
function StatusLED({ status }) {
  const s = (status || "pending").toLowerCase();
  const color = s === "verified" ? GOOD : s === "rejected" ? BAD : COPPER;
  return (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <Box sx={{
        width: 8, height: 8, borderRadius: "50%", backgroundColor: color,
        boxShadow: `0 0 6px ${color}`,
      }} />
      <Typography className="mc-eyebrow" variant="caption" sx={{ color, fontWeight: 600, fontSize: 11 }}>
        {s}
      </Typography>
    </Stack>
  );
}

// ----------------------------------------------------------------------
// Dial gauge — analog-meter style ring showing % returned, with tick
// marks around the face. The signature visual element of this page.
// ----------------------------------------------------------------------
function Dial({ percent = 0, status = "pending", size = 54 }) {
  const pct = Math.max(0, Math.min(100, Number(percent) || 0));
  const color = status === "verified" ? GOOD : status === "rejected" ? BAD : COPPER;
  const r = size / 2 - 6;
  const cx = size / 2;
  const cy = size / 2;

  const ticks = Array.from({ length: 12 }, (_, i) => {
    const angle = (i * 30 - 90) * (Math.PI / 180);
    const isFilled = i <= Math.round((pct / 100) * 12) - 1;
    const inner = r - 3;
    const outer = r + 1;
    return {
      x1: cx + inner * Math.cos(angle), y1: cy + inner * Math.sin(angle),
      x2: cx + outer * Math.cos(angle), y2: cy + outer * Math.sin(angle),
      isFilled,
    };
  });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {ticks.map((t, i) => (
        <line key={i} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
          stroke={t.isFilled ? color : LINE} strokeWidth={1.5} strokeLinecap="round" />
      ))}
      <circle cx={cx} cy={cy} r={r - 6} fill="none" stroke={LINE} strokeWidth={3} />
      <circle
        cx={cx} cy={cy} r={r - 6} fill="none" stroke={color} strokeWidth={3}
        strokeDasharray={2 * Math.PI * (r - 6)} strokeDashoffset={2 * Math.PI * (r - 6) * (1 - pct / 100)}
        strokeLinecap="round" transform={`rotate(-90 ${cx} ${cy})`}
        style={{ transition: "stroke-dashoffset .4s ease" }}
      />
      <text x={cx} y={cy + 4} textAnchor="middle" fontSize={size * 0.24} fontFamily="IBM Plex Mono, monospace"
        fill={TEXT} fontWeight={600}>
        {Math.round(pct)}
      </text>
    </svg>
  );
}

// ----------------------------------------------------------------------
// Stat tile for the summary strip
// ----------------------------------------------------------------------
function StatTile({ label, value, color }) {
  return (
    <Box sx={{
      flex: 1, minWidth: 140, p: 2, borderRadius: 2, backgroundColor: PANEL,
      border: `1px solid ${LINE}`, borderTop: `2px solid ${color}`,
    }}>
      <Typography className="mc-eyebrow" variant="caption" sx={{ color: TEXT_DIM, fontSize: 11 }}>
        {label}
      </Typography>
      <Typography className="mc-mono" variant="h5" sx={{ color, fontWeight: 600, mt: 0.25 }}>
        {value}
      </Typography>
    </Box>
  );
}

export default function MaterialCollectionPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const role = (user?.role || "").toLowerCase();
  const isManager = MANAGERS.includes(role);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);

  const [formDialog, setFormDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ vendor_name: "", material_name: "", quantity: "", purpose: "", project_name: "" });

  const [returnDialog, setReturnDialog] = useState(false);
  const [returnTarget, setReturnTarget] = useState(null);
  const [returnQty, setReturnQty] = useState("");
  const [returnNote, setReturnNote] = useState("");

  const [verifyDialog, setVerifyDialog] = useState(false);
  const [verifyTarget, setVerifyTarget] = useState(null);
  const [verifyDecision, setVerifyDecision] = useState("verified");
  const [verifyRemarks, setVerifyRemarks] = useState("");

  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: "", message: "", onConfirm: null });
  const [menu, setMenu] = useState({ anchorEl: null, row: null });

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");
  const remainingFor = (r) => Number(r.total_after_return ?? (r.quantity - (r.returned_quantity || 0)));

  // ---------------- Load ----------------
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/material-collections");
      setRows(res?.data ?? []);
    } catch (err) {
      enqueueSnackbar("Failed to load material collections.", { variant: "error" });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ---------------- Add / Edit ----------------
  const openAdd = () => {
    setEditingId(null);
    setForm({ vendor_name: "", material_name: "", quantity: "", purpose: "", project_name: "" });
    setFormDialog(true);
  };

  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({
      vendor_name: r.vendor_name || "", material_name: r.material_name || "",
      quantity: r.quantity ?? "", purpose: r.purpose || "", project_name: r.project_name || "",
    });
    setFormDialog(true);
  };

  const submitForm = async () => {
    if (!form.vendor_name.trim() || !form.material_name.trim() || !(Number(form.quantity) > 0)) {
      enqueueSnackbar("Vendor, material, and a positive quantity are required.", { variant: "warning" });
      return;
    }
    setActionLoading(true);
    try {
      if (editingId) {
        await api.patch(`/api/material-collections/${editingId}`, form);
        enqueueSnackbar("Ticket updated — reset to pending for re-verification.", { variant: "success" });
      } else {
        await api.post("/api/material-collections/add", form);
        enqueueSnackbar("Collection logged.", { variant: "success" });
      }
      setFormDialog(false);
      await loadAll();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.error || "Failed to save.", { variant: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  const askDelete = (r) => {
    setConfirmDialog({
      open: true,
      title: "Delete this ticket?",
      message: `"${r.material_name}" from ${r.vendor_name} will be permanently removed, along with any returns logged against it.`,
      onConfirm: async () => {
        setActionLoading(true);
        try {
          await api.delete(`/api/material-collections/${r.id}`);
          enqueueSnackbar("Ticket deleted.", { variant: "info" });
          await loadAll();
        } catch (err) {
          enqueueSnackbar(err?.response?.data?.error || "Failed to delete.", { variant: "error" });
        } finally {
          setActionLoading(false);
          setConfirmDialog({ open: false, title: "", message: "", onConfirm: null });
        }
      },
    });
  };

  // ---------------- Return ----------------
  const openReturn = (r) => {
    setReturnTarget(r); setReturnQty(""); setReturnNote(""); setReturnDialog(true);
  };

  const submitReturn = async () => {
    const qty = Number(returnQty);
    const remaining = remainingFor(returnTarget);
    if (!(qty > 0)) return enqueueSnackbar("Enter a valid return quantity.", { variant: "warning" });
    if (qty > remaining) return enqueueSnackbar(`Cannot exceed the outstanding balance (${remaining}).`, { variant: "warning" });

    setActionLoading(true);
    try {
      await api.post("/api/material-returns/add", { parent_id: returnTarget.id, qty, note: returnNote.trim() || null });
      enqueueSnackbar("Return recorded.", { variant: "success" });
      setReturnDialog(false);
      await loadAll();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.error || "Failed to record return.", { variant: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  // ---------------- Verify ----------------
  const openVerify = (r, decision) => {
    setVerifyTarget(r); setVerifyDecision(decision); setVerifyRemarks(""); setVerifyDialog(true);
  };

  const submitVerify = async () => {
    if (verifyDecision === "rejected" && !verifyRemarks.trim()) {
      return enqueueSnackbar("A remark is required when rejecting.", { variant: "warning" });
    }
    setActionLoading(true);
    try {
      await api.patch(`/api/material-collections/${verifyTarget.id}/verify`, {
        status: verifyDecision, remarks: verifyRemarks.trim() || null,
      });
      enqueueSnackbar(`Marked as ${verifyDecision}.`, { variant: "success" });
      setVerifyDialog(false);
      await loadAll();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.error || "Failed to update.", { variant: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  // ---------------- Filtering ----------------
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let base = rows;
    if (tab === 1 && isManager) base = base.filter((r) => (r.status || "pending") === "pending");
    if (!q) return base;
    return base.filter((r) =>
      [r.vendor_name, r.material_name, r.purpose, r.project_name, r.collector_email, r.status]
        .join(" ").toLowerCase().includes(q)
    );
  }, [rows, search, tab, isManager]);

  const stats = useMemo(() => {
    const pending = rows.filter((r) => (r.status || "pending") === "pending").length;
    const verified = rows.filter((r) => r.status === "verified").length;
    const rejected = rows.filter((r) => r.status === "rejected").length;
    const outstanding = rows
      .filter((r) => r.status !== "rejected")
      .reduce((sum, r) => sum + remainingFor(r), 0);
    return { pending, verified, rejected, outstanding };
  }, [rows]);

  return (
    <ThemeProvider theme={theme}>
      <FontImport />
      <Box sx={{ minHeight: "100vh", background: BG, color: TEXT }}>
        {/* Header */}
        <Box sx={{ borderBottom: `1px solid ${LINE}`, px: { xs: 2, md: 4 }, py: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" spacing={2}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Box onClick={() => navigate(-1)} sx={{ cursor: "pointer", pt: 0.5 }}>
                <ArrowBack sx={{ color: TEXT_DIM, "&:hover": { color: COPPER } }} />
              </Box>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Bolt sx={{ color: COPPER, fontSize: 20 }} />
                  <Typography className="mc-eyebrow" variant="caption" sx={{ color: STEEL, fontSize: 12 }}>
                    SFV Tech · Stock Ops
                  </Typography>
                </Stack>
                <Typography variant="h4" fontWeight={700} sx={{ fontFamily: "Oswald, sans-serif", letterSpacing: "-0.01em" }}>
                  Material Collections
                </Typography>
                <Typography variant="body2" sx={{ color: TEXT_DIM, maxWidth: 480 }}>
                  What left the store, what came back, and what's still outstanding.
                </Typography>
              </Box>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: { xs: "100%", sm: "auto" }, "& > button": { width: { xs: "100%", sm: "auto" } } }}>
              <Button variant="contained" startIcon={<Add />} onClick={openAdd}
                sx={{ backgroundColor: COPPER, color: "#221200", fontWeight: 700, "&:hover": { backgroundColor: "#d6813a" } }}>
                New Collection
              </Button>
              <Button variant="outlined" onClick={loadAll} sx={{ borderColor: LINE, color: TEXT_DIM }}>
                Refresh
              </Button>
              <Button variant="text" startIcon={<Logout />} onClick={logout} sx={{ color: TEXT_DIM }}>
                Logout
              </Button>
            </Stack>
          </Stack>
        </Box>

        {/* Stat strip */}
        <Box sx={{ px: { xs: 2, md: 4 }, pt: 3 }}>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <StatTile label="Pending" value={stats.pending} color={COPPER} />
            <StatTile label="Verified" value={stats.verified} color={GOOD} />
            <StatTile label="Rejected" value={stats.rejected} color={BAD} />
            <StatTile label="Outstanding Qty" value={stats.outstanding} color={STEEL} />
          </Stack>
        </Box>

        {/* Tabs + Search */}
        <Box sx={{ px: { xs: 2, md: 4 }, pt: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" spacing={2}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)}
              TabIndicatorProps={{ style: { background: COPPER } }}
              sx={{ minHeight: 36, "& .MuiTab-root": { minHeight: 36, color: TEXT_DIM, fontFamily: "Oswald, sans-serif", letterSpacing: "0.04em" }, "& .Mui-selected": { color: `${COPPER} !important` } }}>
              <Tab label={isManager ? "All Tickets" : "My Tickets"} />
              {isManager && <Tab label="Pending Verification" />}
            </Tabs>
            <TextField
              value={search} onChange={(e) => setSearch(e.target.value)}
              size="small" placeholder="Search vendor, material, collector…"
              InputProps={{ startAdornment: <Search sx={{ mr: 1, fontSize: 18, color: TEXT_DIM }} /> }}
              sx={{ width: { xs: "100%", sm: "auto" }, minWidth: { xs: 0, sm: 300 }, backgroundColor: PANEL, borderRadius: 1 }}
            />
          </Stack>
        </Box>

        {/* Ledger */}
        <Box sx={{ p: { xs: 2, md: 4 } }}>
          {loading ? (
            <Stack alignItems="center" sx={{ py: 8 }}>
              <CircularProgress sx={{ color: COPPER }} />
            </Stack>
          ) : filtered.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 8, border: `1px dashed ${LINE}`, borderRadius: 2 }}>
              <Typography variant="body1" sx={{ color: TEXT_DIM }}>
                No tickets match your filters.
              </Typography>
              <Typography variant="caption" sx={{ color: TEXT_DIM }}>
                Log a new collection to get started.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.25}>
              {filtered.map((r, i) => {
                const remaining = remainingFor(r);
                const pct = Number(r.returned_percent || 0);
                const mine = r.collector_email === user?.email;
                const status = r.status || "pending";
                const canEdit = (mine || isManager) && status !== "verified";
                const canDelete = (mine && status === "pending") || isManager;
                const canReturn = remaining > 0 && status !== "rejected" && (mine || isManager);
                const canVerify = isManager && status === "pending";
                const hasMenu = canEdit || canDelete || canVerify;
                const accent = status === "verified" ? GOOD : status === "rejected" ? BAD : COPPER;

                return (
                  <Box key={r.id} className="mc-row"
                    style={{ animationDelay: `${Math.min(i, 12) * 30}ms` }}
                    sx={{
                      display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "stretch", sm: "center" }, gap: 2, p: { xs: 1.5, sm: 2 },
                      backgroundColor: PANEL, border: `1px solid ${LINE}`, borderLeft: `3px solid ${accent}`,
                      borderRadius: 2, transition: "box-shadow .15s, border-color .15s",
                    }}>
                    <Box sx={{ alignSelf: { xs: "center", sm: "auto" } }}><Dial percent={pct} status={status} /></Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography fontWeight={600}>{r.material_name}</Typography>
                        <Typography variant="caption" sx={{ color: TEXT_DIM }}>from {r.vendor_name}</Typography>
                      </Stack>
                      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ mt: 0.25 }}>
                        <Typography className="mc-mono" variant="caption" sx={{ color: STEEL }}>
                          {r.collector_email}
                        </Typography>
                        <Typography className="mc-mono" variant="caption" sx={{ color: TEXT_DIM }}>
                          {fmtDate(r.collection_date)}
                        </Typography>
                        {r.project_name && (
                          <Typography variant="caption" sx={{
                            color: TEXT_DIM, backgroundColor: STEEL_DIM, px: 0.75, borderRadius: 0.5,
                          }}>
                            {r.project_name}
                          </Typography>
                        )}
                      </Stack>
                      {r.purpose && (
                        <Typography variant="caption" sx={{ color: TEXT_DIM, display: "block", mt: 0.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {r.purpose}
                        </Typography>
                      )}
                      {r.remarks && (
                        <Typography variant="caption" sx={{ color: accent, display: "block", mt: 0.25 }}>
                          "{r.remarks}"
                        </Typography>
                      )}
                    </Box>

                    <Stack alignItems="flex-end" spacing={0.5} sx={{ minWidth: { xs: 0, sm: 96 }, alignItems: { xs: "flex-start", sm: "flex-end" } }}>
                      <Typography className="mc-mono" variant="body2" fontWeight={600}>
                        {r.returned_quantity || 0} / {r.quantity}
                      </Typography>
                      <StatusLED status={status} />
                    </Stack>

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={0.5} alignItems="center" sx={{ width: { xs: "100%", sm: "auto" }, "& > button": { width: { xs: "100%", sm: "auto" } } }}>
                      {canReturn && (
                        <Button size="small" startIcon={<Undo sx={{ fontSize: 16 }} />}
                          onClick={() => openReturn(r)}
                          sx={{ color: STEEL, border: `1px solid ${STEEL}`, borderRadius: 1.5, px: 1.25, fontSize: 12 }}>
                          Return
                        </Button>
                      )}
                      {hasMenu && (
                        <Box onClick={(e) => setMenu({ anchorEl: e.currentTarget, row: r })} sx={{ cursor: "pointer", color: TEXT_DIM, "&:hover": { color: TEXT }, display: "flex" }}>
                          <MoreVert />
                        </Box>
                      )}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>

        {/* Row action menu */}
        <Menu
          anchorEl={menu.anchorEl}
          open={Boolean(menu.anchorEl)}
          onClose={() => setMenu({ anchorEl: null, row: null })}
        >
          {menu.row && (() => {
            const r = menu.row;
            const mine = r.collector_email === user?.email;
            const status = r.status || "pending";
            const canEdit = (mine || isManager) && status !== "verified";
            const canDelete = (mine && status === "pending") || isManager;
            const canVerify = isManager && status === "pending";
            const items = [];
            if (canEdit) items.push(
              <MenuItem key="edit" onClick={() => { openEdit(r); setMenu({ anchorEl: null, row: null }); }}>
                <ListItemIcon><Edit fontSize="small" sx={{ color: STEEL }} /></ListItemIcon>
                <ListItemText>Edit ticket</ListItemText>
              </MenuItem>
            );
            if (canVerify) items.push(
              <MenuItem key="verify" onClick={() => { openVerify(r, "verified"); setMenu({ anchorEl: null, row: null }); }}>
                <ListItemIcon><CheckCircle fontSize="small" sx={{ color: GOOD }} /></ListItemIcon>
                <ListItemText>Verify</ListItemText>
              </MenuItem>,
              <MenuItem key="reject" onClick={() => { openVerify(r, "rejected"); setMenu({ anchorEl: null, row: null }); }}>
                <ListItemIcon><Cancel fontSize="small" sx={{ color: BAD }} /></ListItemIcon>
                <ListItemText>Reject</ListItemText>
              </MenuItem>
            );
            if (canDelete) items.push(
              <MenuItem key="delete" onClick={() => { askDelete(r); setMenu({ anchorEl: null, row: null }); }}>
                <ListItemIcon><Delete fontSize="small" sx={{ color: BAD }} /></ListItemIcon>
                <ListItemText>Delete</ListItemText>
              </MenuItem>
            );
            return items;
          })()}
        </Menu>

        {/* Add / Edit Dialog */}
        <Dialog open={formDialog} onClose={() => setFormDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontFamily: "Oswald, sans-serif" }}>
            {editingId ? "Edit Ticket" : "New Material Collection"}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Vendor Name" fullWidth value={form.vendor_name}
                onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} />
              <TextField label="Material Name" fullWidth value={form.material_name}
                onChange={(e) => setForm({ ...form, material_name: e.target.value })} />
              <TextField label="Quantity" type="number" fullWidth value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })} inputProps={{ min: 1 }} />
              <TextField label="Project Name (optional)" fullWidth value={form.project_name}
                onChange={(e) => setForm({ ...form, project_name: e.target.value })} />
              <TextField label="Purpose (optional)" fullWidth multiline minRows={2} value={form.purpose}
                onChange={(e) => setForm({ ...form, purpose: e.target.value })} />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 2, flexDirection: { xs: "column-reverse", sm: "row" }, gap: 1, "& > button": { width: { xs: "100%", sm: "auto" } } }}>
            <Button onClick={() => setFormDialog(false)} sx={{ color: TEXT_DIM }}>Cancel</Button>
            <Button variant="contained" onClick={submitForm} disabled={actionLoading}
              sx={{ backgroundColor: COPPER, color: "#221200", "&:hover": { backgroundColor: "#d6813a" } }}>
              {actionLoading ? "Saving…" : editingId ? "Save Changes" : "Record Collection"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Return Dialog */}
        <Dialog open={returnDialog} onClose={() => setReturnDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontFamily: "Oswald, sans-serif" }}>Record Return</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2, color: STEEL }}>
              {returnTarget?.material_name} — outstanding balance:{" "}
              <span className="mc-mono">{returnTarget ? remainingFor(returnTarget) : 0} of {returnTarget?.quantity}</span>
            </Typography>
            <Stack spacing={2}>
              <TextField label="Return Quantity" type="number" fullWidth value={returnQty}
                onChange={(e) => setReturnQty(e.target.value)}
                inputProps={{ min: 1, max: returnTarget ? remainingFor(returnTarget) : undefined }} />
              <TextField label="Note (optional)" fullWidth multiline minRows={2} value={returnNote}
                onChange={(e) => setReturnNote(e.target.value)} />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 2, flexDirection: { xs: "column-reverse", sm: "row" }, gap: 1, "& > button": { width: { xs: "100%", sm: "auto" } } }}>
            <Button onClick={() => setReturnDialog(false)} sx={{ color: TEXT_DIM }}>Cancel</Button>
            <Button variant="contained" onClick={submitReturn} disabled={actionLoading}
              sx={{ backgroundColor: STEEL, color: "#052027", "&:hover": { backgroundColor: "#5a9fb3" } }}>
              {actionLoading ? "Saving…" : "Confirm Return"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Verify Dialog */}
        <Dialog open={verifyDialog} onClose={() => setVerifyDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontFamily: "Oswald, sans-serif" }}>
            {verifyDecision === "verified" ? "Verify Ticket" : "Reject Ticket"}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2, color: STEEL }}>
              {verifyTarget?.material_name} — collected by <span className="mc-mono">{verifyTarget?.collector_email}</span>
            </Typography>
            <TextField
              label={verifyDecision === "rejected" ? "Reason for rejection (required)" : "Remark (optional)"}
              fullWidth multiline minRows={3} value={verifyRemarks}
              onChange={(e) => setVerifyRemarks(e.target.value)}
            />
          </DialogContent>
          <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 2, flexDirection: { xs: "column-reverse", sm: "row" }, gap: 1, "& > button": { width: { xs: "100%", sm: "auto" } } }}>
            <Button onClick={() => setVerifyDialog(false)} sx={{ color: TEXT_DIM }}>Cancel</Button>
            <Button variant="contained" onClick={submitVerify} disabled={actionLoading}
              sx={{ backgroundColor: verifyDecision === "verified" ? GOOD : BAD, color: "#0d1210" }}>
              {actionLoading ? "Saving…" : verifyDecision === "verified" ? "Confirm Verify" : "Confirm Reject"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirm Dialog (delete, etc.) */}
        <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ ...confirmDialog, open: false })} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontFamily: "Oswald, sans-serif" }}>{confirmDialog.title}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: TEXT_DIM }}>{confirmDialog.message}</Typography>
          </DialogContent>
          <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 2, flexDirection: { xs: "column-reverse", sm: "row" }, gap: 1, "& > button": { width: { xs: "100%", sm: "auto" } } }}>
            <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })} sx={{ color: TEXT_DIM }}>Cancel</Button>
            <Button variant="contained" color="error" onClick={confirmDialog.onConfirm} disabled={actionLoading}>
              {actionLoading ? "Working…" : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
}